// The installable portal (2026-10-22, docs/PWA.md).
//   - registers the service worker (/sw.js, built by vite.config.ts) in production builds;
//   - a new version waits until the user clicks "Reload" in the update banner (no surprise reload during a call);
//   - "Install app": the browser's own install prompt (Chrome, Edge, Android), or the Add to Home Screen steps
//     on iPhone / iPad, where Safari has no prompt;
//   - an "offline" banner while the device has no network (live data needs the server).
import * as React from 'react';
import { create } from 'zustand';
import { Download, RefreshCw, WifiOff, Share, SquarePlus } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent } from '@/components/ui/dialog';
import { t } from '@/i18n';

interface InstallPromptEvent extends Event { prompt: () => Promise<void>; userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }> }

interface PwaState {
  install: InstallPromptEvent | null;       // the browser's deferred install prompt
  waiting: ServiceWorker | null;            // a new version, installed and waiting
  offline: boolean;
  installed: boolean;                        // running as the installed app
}
export const usePwa = create<PwaState>(() => ({
  install: null, waiting: null, offline: typeof navigator !== 'undefined' && !navigator.onLine,
  installed: typeof window !== 'undefined' && (window.matchMedia?.('(display-mode: standalone)').matches || (navigator as { standalone?: boolean }).standalone === true),
}));

// iPhone / iPad Safari (also iPadOS, which reports itself as a Mac with touch)
export function isIos(): boolean {
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
}

let started = false;
let updateRequested = false;
let reloading = false;
export function startPwa(): void {
  if (started || typeof window === 'undefined') return;
  started = true;
  window.addEventListener('beforeinstallprompt', (e) => { e.preventDefault(); usePwa.setState({ install: e as InstallPromptEvent }); });
  window.addEventListener('appinstalled', () => usePwa.setState({ install: null, installed: true }));
  window.addEventListener('online', () => usePwa.setState({ offline: false }));
  window.addEventListener('offline', () => usePwa.setState({ offline: true }));
  if (!('serviceWorker' in navigator) || !import.meta.env.PROD) return;
  // reload only for an update the user asked for: the first install also changes the controller (clients.claim)
  navigator.serviceWorker.addEventListener('controllerchange', () => {
    if (updateRequested && !reloading) { reloading = true; window.location.reload(); }
  });
  navigator.serviceWorker.register('/sw.js').then((reg) => {
    const watch = (w: ServiceWorker | null) => w?.addEventListener('statechange', () => {
      // a waiting worker while a page is already controlled is an update (the first install needs no reload)
      if (w.state === 'installed' && navigator.serviceWorker.controller) usePwa.setState({ waiting: w });
    });
    if (reg.waiting && navigator.serviceWorker.controller) usePwa.setState({ waiting: reg.waiting });
    watch(reg.installing);
    reg.addEventListener('updatefound', () => watch(reg.installing));
    // installed apps stay open for days: look for a new version every hour
    setInterval(() => reg.update().catch(() => {}), 60 * 60 * 1000);
  }).catch(() => { /* no service worker (http on a non-local address): the portal works as a normal site */ });
}

export function applyUpdate(): void {
  const w = usePwa.getState().waiting;
  if (!w) return;
  updateRequested = true;
  w.postMessage('SKIP_WAITING');               // controllerchange then reloads the page
}

// "Install app": hidden when installed, or when the browser can do neither
export function InstallButton({ compact }: { compact?: boolean }) {
  const { install, installed } = usePwa();
  const [iosHelp, setIosHelp] = React.useState(false);
  if (installed) return null;
  const ios = isIos();
  if (!install && !ios) return null;
  async function click() {
    if (install) {
      await install.prompt();
      const choice = await install.userChoice.catch(() => null);
      if (choice?.outcome === 'accepted') usePwa.setState({ install: null });
    } else setIosHelp(true);
  }
  return (<>
    <Button variant="outline" size="sm" onClick={click} data-testid="install-app" aria-label={t('Install app')} title={t('Install app')}>
      <Download /> {!compact && <span className="hidden sm:inline">{t('Install app')}</span>}
    </Button>
    <Dialog open={iosHelp} onOpenChange={setIosHelp}>
      {iosHelp && <DialogContent title={t('Install app')} className="max-w-sm">
        <ol className="grid gap-3 text-sm" data-testid="ios-install-steps">
          <li className="flex items-center gap-2"><Share className="h-5 w-5 shrink-0" /> {t('Tap the Share button in Safari.')}</li>
          <li className="flex items-center gap-2"><SquarePlus className="h-5 w-5 shrink-0" /> {t('Choose Add to Home Screen, then Add.')}</li>
        </ol>
      </DialogContent>}
    </Dialog>
  </>);
}

// the update and offline banners, at the top of every page
export function PwaBanners() {
  const { waiting, offline } = usePwa();
  React.useEffect(() => startPwa(), []);
  return (<>
    {offline && (
      <div className="flex items-center gap-2 bg-warning/20 px-4 py-1.5 text-sm" role="status" data-testid="offline-banner">
        <WifiOff className="h-4 w-4 shrink-0" /> {t('No network connection. Calls and live data come back when the device is online again.')}
      </div>
    )}
    {waiting && (
      <div className="flex flex-wrap items-center gap-2 bg-primary px-4 py-1.5 text-sm text-primary-foreground" role="status" data-testid="update-banner">
        <span className="flex-1">{t('A new version of the portal is ready.')}</span>
        <Button size="sm" variant="secondary" onClick={applyUpdate}><RefreshCw /> {t('Reload')}</Button>
      </div>
    )}
  </>);
}
